import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, AlertCircle, Loader2, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { API } from '@/utils/api';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AppointmentSelection = ({ bookingData, onUpdate }) => {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(bookingData.preferredDate || '');
  const [weeksLoaded, setWeeksLoaded] = useState(26); // Start with 6 months
  const [responseTime, setResponseTime] = useState(null);
  const abortControllerRef = useRef(null);

  // Debounce postcode to avoid rapid API calls
  const debouncedPostcode = useDebounce(bookingData.postcode, 500);

  useEffect(() => {
    // Only fetch if we have a valid postcode (at least 3 characters)
    if (debouncedPostcode && debouncedPostcode.trim().length >= 3) {
      fetchAvailability();
    } else if (!debouncedPostcode || debouncedPostcode.trim().length < 3) {
      setAvailability([]);
      setError(null);
    }

    // Cleanup: abort any pending request when component unmounts or postcode changes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // fetchAvailability is intentionally excluded — only postcode changes should
    // trigger a refetch. weeksLoaded changes are driven via loadMoreDates directly.
  }, [debouncedPostcode]);

  const fetchAvailability = useCallback(async (additionalWeeks = null) => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (additionalWeeks) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const weeksToFetch = additionalWeeks || weeksLoaded;
      const startTime = Date.now();

      const response = await axios.post(`${API}/availability`, {
        postcode: bookingData.postcode,
        weeks: weeksToFetch
      }, {
        signal: abortControllerRef.current.signal,
        timeout: 30000 // 30 second timeout
      });

      const clientTime = Date.now() - startTime;
      setResponseTime(response.data.responseTime || `${clientTime}ms`);

      setAvailability(response.data.availability || []);

      if (additionalWeeks) {
        setWeeksLoaded(weeksToFetch);
      }

      if (response.data.message && response.data.availability?.length === 0) {
        setError(response.data.message);
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching availability:', err);
      setError('Failed to load available appointments. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [bookingData.postcode, weeksLoaded]);

  const loadMoreDates = () => {
    const newWeeksTotal = weeksLoaded + 26; // Load 6 more months
    fetchAvailability(newWeeksTotal);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    onUpdate({ preferredDate: date, preferredTime: '' });
  };

  const handleTimeChange = (time) => {
    onUpdate({ preferredTime: time });
  };

  const getAvailableSlotsForDate = () => {
    if (!selectedDate) return [];
    const dateData = availability.find(d => d.date === selectedDate);
    return dateData ? dateData.slots : [];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    return `${weekday}, ${day} ${month}`;
  };

  // Show skeleton loading state
  if (loading) {
    return (
      <div data-testid="appointment-selection-step">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Appointment</h2>
        <p className="text-gray-600 mb-6">Choose your preferred date and time</p>
        
        <div className="space-y-4">
          {/* Skeleton for info alert */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-blue-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-blue-100 rounded w-1/2"></div>
          </div>
          
          {/* Skeleton for date selector */}
          <div>
            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-10 bg-gray-100 rounded w-full animate-pulse"></div>
          </div>
          
          {/* Loading indicator */}
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
            <span className="ml-3 text-gray-600">Loading available appointments...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="appointment-selection-step">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Appointment</h2>
      <p className="text-gray-600 mb-6">Choose your preferred date and time</p>

      {/* No postcode entered yet */}
      {(!bookingData.postcode || bookingData.postcode.trim().length < 3) && (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Please complete the previous step with your full postcode to see available appointments.
          </AlertDescription>
        </Alert>
      )}

      {/* Error with no availability */}
      {!loading && error && availability.length === 0 && bookingData.postcode?.length >= 3 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>No appointments available right now.</strong>
            <br />
            Please <a href="/contact" className="underline font-medium">contact us</a> or try again later.
          </AlertDescription>
        </Alert>
      )}

      {availability.length > 0 && (
        <div className="space-y-6">
          <Alert className="bg-blue-50 border-blue-200">
            <Calendar className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Appointments available on Tuesdays and Thursdays only.</strong>
              <br />
              Each appointment is a 1-hour slot. Select your preferred date and time below.
              <br />
              <span className="text-sm mt-2 block">Other dates may be available on request. Please <a href="/contact" className="underline font-medium">contact us</a>.</span>
            </AlertDescription>
          </Alert>

          <div>
            <Label htmlFor="date" className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4" />
              Select Date *
            </Label>
            <Select
              value={selectedDate}
              onValueChange={handleDateChange}
            >
              <SelectTrigger data-testid="date-select" className="w-full">
                <SelectValue placeholder="Choose a date" />
              </SelectTrigger>
              <SelectContent>
                {availability.map(dateData => (
                  <SelectItem key={dateData.date} value={dateData.date}>
                    {formatDate(dateData.date)} - {dateData.slots.length} slot{dateData.slots.length !== 1 ? 's' : ''} available
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedDate && (
            <div>
              <Label htmlFor="time" className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4" />
                Select Time *
              </Label>
              <Select
                value={bookingData.preferredTime}
                onValueChange={handleTimeChange}
              >
                <SelectTrigger data-testid="time-select" className="w-full">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableSlotsForDate().map(slot => (
                    <SelectItem key={slot.time} value={slot.time}>
                      {slot.time_12hr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {getAvailableSlotsForDate().length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No slots available for this date. Please choose another date.
                </p>
              )}
            </div>
          )}

          <div className="text-center">
            <button
              onClick={loadMoreDates}
              disabled={loadingMore}
              className="text-blue-600 hover:text-blue-700 underline text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading more dates...
                </span>
              ) : (
                `Need a date further ahead? Load more dates (showing next ${weeksLoaded} weeks)`
              )}
            </button>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Available times are calculated based on your location and existing bookings. 
              Your appointment will be confirmed once we receive your booking.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentSelection;
